import { createTheme } from "@mui/material";

// Light auth theme
export const lightAuthTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: "#6366F1", // Purple/indigo color for primary elements
      light: "#818CF8",
      dark: "#4F46E5",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#3B82F6", // Blue for secondary elements like links
      light: "#60A5FA",
      dark: "#2563EB",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#F9FAFB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1F2937", // Dark gray for main text
      secondary: "#6B7280", // Medium gray for secondary text
      disabled: "#9CA3AF",
    },
    grey: {
      50: "#F9FAFB",
      100: "#F3F4F6",
      200: "#E5E7EB",
      300: "#D1D5DB",
      400: "#9CA3AF",
      500: "#6B7280",
      600: "#4B5563",
      700: "#374151",
      800: "#1F2937",
      900: "#111827",
    },
    divider: "#E5E7EB",
    success: {
      main: "#10B981",
      light: "#34D399",
      dark: "#059669",
    },
    error: {
      main: "#EF4444",
      light: "#F87171",
      dark: "#DC2626",
    },
    warning: {
      main: "#F59E0B",
      light: "#FBBF24",
      dark: "#D97706",
    },
    info: {
      main: "#3B82F6",
      light: "#60A5FA",
      dark: "#2563EB",
    },
  },
  typography: {
    fontFamily: `'Inter', 'SF Pro Display', 'Roboto', 'Arial', sans-serif`,
    h1: {
      fontSize: "2.5rem",
      fontWeight: 700,
      lineHeight: 1.2,
      color: "#1F2937",
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 600,
      lineHeight: 1.3,
      color: "#1F2937",
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#1F2937",
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#1F2937",
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#1F2937",
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#1F2937",
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: 1.5,
      color: "#6B7280",
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.5,
      color: "#6B7280",
    },
    button: {
      fontSize: "1rem",
      fontWeight: 600,
      textTransform: "none",
      lineHeight: 1.5,
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: 1.5,
      color: "#9CA3AF",
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          padding: "2rem 1rem",
          "@media (min-width: 600px)": {
            padding: "3rem 2rem",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            backgroundColor: "#F9FAFB",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#FFFFFF",
              borderColor: "#D1D5DB",
            },
            "&.Mui-focused": {
              backgroundColor: "#FFFFFF",
              borderColor: "#6366F1",
              borderWidth: "2px",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            },
            "&.Mui-error": {
              borderColor: "#EF4444",
              backgroundColor: "#FEF2F2",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#6B7280",
            fontWeight: 500,
            "&.Mui-focused": {
              color: "#6366F1",
            },
          },
          "& .MuiInputBase-input": {
            padding: "16px 20px",
            fontSize: "1rem",
            fontWeight: 500,
            color: "#1F2937",
            "&::placeholder": {
              color: "#9CA3AF",
              opacity: 1,
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: '#FFFFFF',
          border: 'none',
          '&:hover': {
            background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
          },
        },
        outlined: {
          border: '2px solid #1976d2',
          color: '#1976d2',
          background: 'transparent',
          '&:hover': {
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            color: '#FFFFFF',
            border: '2px solid transparent',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
          },
        },
        text: {
          color: '#1976d2',
          '&:hover': {
            background: 'rgba(25, 118, 210, 0.1)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: "#3B82F6",
          textDecoration: "none",
          fontWeight: 500,
          transition: "color 0.2s ease-in-out",
          "&:hover": {
            color: "#2563EB",
            textDecoration: "underline",
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: "1px solid",
        },
        standardError: {
          backgroundColor: "#FEF2F2",
          borderColor: "#FECACA",
          color: "#DC2626",
        },
        standardSuccess: {
          backgroundColor: "#F0FDF4",
          borderColor: "#BBF7D0",
          color: "#059669",
        },
        standardInfo: {
          backgroundColor: "#EFF6FF",
          borderColor: "#BFDBFE",
          color: "#2563EB",
        },
        standardWarning: {
          backgroundColor: "#FFFBEB",
          borderColor: "#FED7AA",
          color: "#D97706",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#6B7280",
          "&:hover": {
            backgroundColor: "rgba(99, 102, 241, 0.04)",
            color: "#6366F1",
          },
        },
      },
    },
  },
});

// Dark auth theme
export const darkAuthTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: "#818CF8", // Lighter purple for dark mode
      light: "#A5B4FC",
      dark: "#6366F1",
      contrastText: "#000000",
    },
    secondary: {
      main: "#60A5FA", // Lighter blue for dark mode
      light: "#93C5FD",
      dark: "#3B82F6",
      contrastText: "#000000",
    },
    background: {
      default: "#111827",
      paper: "#1F2937",
    },
    text: {
      primary: "#F9FAFB", // Light gray for main text
      secondary: "#D1D5DB", // Medium light gray for secondary text
      disabled: "#6B7280",
    },
    grey: {
      50: "#111827",
      100: "#1F2937",
      200: "#374151",
      300: "#4B5563",
      400: "#6B7280",
      500: "#9CA3AF",
      600: "#D1D5DB",
      700: "#E5E7EB",
      800: "#F3F4F6",
      900: "#F9FAFB",
    },
    divider: "#374151",
    success: {
      main: "#34D399",
      light: "#6EE7B7",
      dark: "#10B981",
    },
    error: {
      main: "#F87171",
      light: "#FCA5A5",
      dark: "#EF4444",
    },
    warning: {
      main: "#FBBF24",
      light: "#FCD34D",
      dark: "#F59E0B",
    },
    info: {
      main: "#60A5FA",
      light: "#93C5FD",
      dark: "#3B82F6",
    },
  },
  typography: {
    fontFamily: `'Inter', 'SF Pro Display', 'Roboto', 'Arial', sans-serif`,
    h1: {
      fontSize: "2.5rem",
      fontWeight: 700,
      lineHeight: 1.2,
      color: "#F9FAFB",
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 600,
      lineHeight: 1.3,
      color: "#F9FAFB",
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#F9FAFB",
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#F9FAFB",
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#F9FAFB",
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#F9FAFB",
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: 1.5,
      color: "#D1D5DB",
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.5,
      color: "#D1D5DB",
    },
    button: {
      fontSize: "1rem",
      fontWeight: 600,
      textTransform: "none",
      lineHeight: 1.5,
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: 1.5,
      color: "#9CA3AF",
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          padding: "2rem 1rem",
          "@media (min-width: 600px)": {
            padding: "3rem 2rem",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            backgroundColor: "#374151",
            border: "1px solid #4B5563",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#4B5563",
              borderColor: "#6B7280",
            },
            "&.Mui-focused": {
              backgroundColor: "#4B5563",
              borderColor: "#818CF8",
              borderWidth: "2px",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            },
            "&.Mui-error": {
              borderColor: "#F87171",
              backgroundColor: "#7F1D1D",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#D1D5DB",
            fontWeight: 500,
            "&.Mui-focused": {
              color: "#818CF8",
            },
          },
          "& .MuiInputBase-input": {
            padding: "16px 20px",
            fontSize: "1rem",
            fontWeight: 500,
            color: "#F9FAFB",
            "&::placeholder": {
              color: "#9CA3AF",
              opacity: 1,
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "1rem",
          padding: "16px 32px",
          height: "56px",
          boxShadow: "none",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(129, 140, 248, 0.3)",
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
          color: '#FFFFFF',
          border: 'none',
          '&:hover': {
            background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(21, 101, 192, 0.4)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 4px 15px rgba(21, 101, 192, 0.4)',
          },
          '&:disabled': {
            background: '#4B5563',
            color: '#6B7280',
            transform: 'none',
            boxShadow: 'none',
          },
        },
        outlined: {
          border: '2px solid #60A5FA',
          color: '#60A5FA',
          background: 'transparent',
          '&:hover': {
            background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
            color: '#FFFFFF',
            border: '2px solid transparent',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(21, 101, 192, 0.4)',
          },
        },
        text: {
          color: '#60A5FA',
          '&:hover': {
            background: 'rgba(96, 165, 250, 0.1)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: "#60A5FA",
          textDecoration: "none",
          fontWeight: 500,
          transition: "color 0.2s ease-in-out",
          "&:hover": {
            color: "#93C5FD",
            textDecoration: "underline",
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: "1px solid",
        },
        standardError: {
          backgroundColor: "#7F1D1D",
          borderColor: "#991B1B",
          color: "#FCA5A5",
        },
        standardSuccess: {
          backgroundColor: "#064E3B",
          borderColor: "#065F46",
          color: "#6EE7B7",
        },
        standardInfo: {
          backgroundColor: "#1E3A8A",
          borderColor: "#1E40AF",
          color: "#93C5FD",
        },
        standardWarning: {
          backgroundColor: "#78350F",
          borderColor: "#92400E",
          color: "#FCD34D",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#D1D5DB",
          "&:hover": {
            backgroundColor: "rgba(129, 140, 248, 0.04)",
            color: "#818CF8",
          },
        },
      },
    },
  },
});

// Default export for backward compatibility (uses light theme)
export const authTheme = lightAuthTheme; 